import {
  Accordion,
  Badge,
  Box,
  Button,
  Group,
  Image,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import type { LucideIcon } from 'lucide-react';
import {
  Accessibility,
  ChevronDown,
  Ruler,
  Scale,
  Shirt,
  Trash2,
  UserRound,
} from 'lucide-react';
import type { BodyImageComparison } from '@types';
import { formatDateTimeValue } from '../patients/utils/formatters';
import {
  formatBodyImageMetricValue,
  getBodyImageComparisonMetricEntries,
  getBodyImageFigurePlaceholderSrc,
  getPlaceholderFigureFromParameters,
} from './utils';

const METRIC_ICON_BY_LABEL: Record<string, LucideIcon> = {
  Peso: Scale,
  Spalle: Accessibility,
  Fianchi: Ruler,
  Vita: Ruler,
  Petto: Shirt,
  Collo: UserRound,
  Coscia: Ruler,
  Polpacci: Ruler,
};

interface BodyImageComparisonMeasurementsGridProps {
  parameters: Record<string, unknown>;
}

export function BodyImageComparisonMeasurementsGrid({
  parameters,
}: BodyImageComparisonMeasurementsGridProps) {
  const entries = getBodyImageComparisonMetricEntries(parameters);

  if (entries.length === 0) {
    return (
      <Text size='sm' c='dimmed'>
        Nessuna misura registrata per questo manichino.
      </Text>
    );
  }

  return (
    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing='sm'>
      {entries.map(({ definition, value }) => {
        const FieldIcon = METRIC_ICON_BY_LABEL[definition.label] ?? Ruler;

        return (
          <Paper key={definition.key} withBorder radius='sm' p='sm' bg='gray.0'>
            <Group
              justify='space-between'
              align='center'
              wrap='nowrap'
              gap='md'
            >
              <Group
                align='center'
                gap='sm'
                wrap='nowrap'
                style={{ minWidth: 0 }}
              >
                <ThemeIcon size='md' radius='xl' variant='light' color='blue'>
                  <FieldIcon size={14} strokeWidth={1.8} />
                </ThemeIcon>
                <Text size='sm' fw={600} truncate>
                  {definition.label}
                </Text>
              </Group>
              <Text size='sm' fw={600} style={{ whiteSpace: 'nowrap' }}>
                {formatBodyImageMetricValue(definition.key, value)}
              </Text>
            </Group>
          </Paper>
        );
      })}
    </SimpleGrid>
  );
}

interface BodyImageComparisonAccordionListProps {
  comparisons: BodyImageComparison[];
  activeComparisonId?: number | null;
  showPreview?: boolean;
  showDeviceAction?: boolean;
  isDeviceActionDisabled?: boolean;
  isDeleteDisabled?: boolean;
  isDeletePending?: boolean;
  onDelete?: (comparison: BodyImageComparison) => void;
  onShowMannequin?: (comparison: BodyImageComparison) => void;
}

const openDeleteComparisonConfirmModal = (
  comparison: BodyImageComparison,
  onDelete: (comparison: BodyImageComparison) => void
) => {
  const label = comparison.label?.trim() || `Manichino #${comparison.id}`;

  modals.openConfirmModal({
    title: 'Elimina manichino',
    centered: true,
    children: (
      <Text size='sm'>
        Eliminare il manichino &ldquo;{label}&rdquo;? L&apos;operazione non è
        reversibile.
      </Text>
    ),
    labels: { confirm: 'Elimina', cancel: 'Annulla' },
    confirmProps: { color: 'red' },
    onConfirm: () => {
      onDelete(comparison);
    },
  });
};

export function BodyImageComparisonAccordionList({
  comparisons,
  activeComparisonId = null,
  showPreview = false,
  showDeviceAction = false,
  isDeviceActionDisabled = false,
  isDeleteDisabled = false,
  isDeletePending = false,
  onDelete,
  onShowMannequin,
}: BodyImageComparisonAccordionListProps) {
  if (comparisons.length === 0) {
    return null;
  }

  return (
    <Accordion
      variant='separated'
      radius='sm'
      chevronPosition='right'
      chevron={<ChevronDown size={14} strokeWidth={2} aria-hidden />}
    >
      {comparisons.map(comparison => {
        const label = comparison.label?.trim() || `Manichino #${comparison.id}`;
        const metricCount = getBodyImageComparisonMetricEntries(
          comparison.parameters
        ).length;
        const previewFigure = getPlaceholderFigureFromParameters(
          comparison.parameters
        );
        const isActiveOnVisor = activeComparisonId === comparison.id;

        return (
          <Accordion.Item key={comparison.id} value={String(comparison.id)}>
            <Accordion.Control>
              <Group
                align='center'
                justify='space-between'
                wrap='nowrap'
                gap='sm'
                w='100%'
                pr='xs'
              >
                <Group
                  align='center'
                  wrap='nowrap'
                  gap='sm'
                  style={{ minWidth: 0 }}
                >
                  {showPreview ? (
                    <Box
                      w={48}
                      style={{
                        flexShrink: 0,
                        aspectRatio: '3 / 4',
                        overflow: 'hidden',
                        borderRadius: 'var(--mantine-radius-sm)',
                        backgroundColor: 'var(--mantine-color-gray-1)',
                        outline: isActiveOnVisor
                          ? '2px solid var(--mantine-color-teal-5)'
                          : undefined,
                      }}
                    >
                      <Image
                        src={getBodyImageFigurePlaceholderSrc(previewFigure)}
                        alt={label}
                        fit='contain'
                        w='100%'
                        h='100%'
                      />
                    </Box>
                  ) : null}
                  <Stack gap={2} style={{ minWidth: 0 }}>
                    <Group gap='xs' wrap='wrap' align='center'>
                      <Text fw={600} truncate>
                        {label}
                      </Text>
                      {isActiveOnVisor ? (
                        <Badge size='xs' variant='light' color='teal'>
                          In comparazione sul visore
                        </Badge>
                      ) : null}
                    </Group>
                    <Text size='xs' c='dimmed'>
                      {comparison.createdAt
                        ? formatDateTimeValue(comparison.createdAt)
                        : 'Data non disponibile'}
                      {metricCount > 0
                        ? ` · ${metricCount} misur${metricCount === 1 ? 'a' : 'e'}`
                        : ''}
                    </Text>
                  </Stack>
                </Group>

                <Group
                  gap='xs'
                  wrap='nowrap'
                  align='center'
                  style={{ flexShrink: 0 }}
                >
                  {showDeviceAction && onShowMannequin ? (
                    <Button
                      size='sm'
                      variant={isActiveOnVisor ? 'filled' : 'default'}
                      color={isActiveOnVisor ? 'teal' : 'gray'}
                      disabled={isDeviceActionDisabled}
                      onMouseDown={event => {
                        event.stopPropagation();
                      }}
                      onClick={event => {
                        event.stopPropagation();
                        event.preventDefault();
                        onShowMannequin(comparison);
                      }}
                    >
                      Mostra manichino
                    </Button>
                  ) : null}
                  <Text
                    size='xs'
                    c='dimmed'
                    fw={500}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    Mostra dettagli
                  </Text>
                </Group>
              </Group>
            </Accordion.Control>

            <Accordion.Panel>
              <Stack gap='md'>
                <BodyImageComparisonMeasurementsGrid
                  parameters={comparison.parameters}
                />

                <Paper withBorder p='md' radius='sm'>
                  <Stack gap='xs'>
                    <Text fw={600}>Note manichino</Text>
                    <Text
                      size='sm'
                      c={comparison.notes?.trim() ? undefined : 'dimmed'}
                    >
                      {comparison.notes?.trim() || 'Nessuna nota'}
                    </Text>
                  </Stack>
                </Paper>

                {onDelete ? (
                  <Group justify='flex-end'>
                    <Button
                      size='sm'
                      color='red'
                      variant='subtle'
                      leftSection={<Trash2 size={14} />}
                      disabled={isDeleteDisabled || isDeletePending}
                      onClick={() => {
                        openDeleteComparisonConfirmModal(comparison, onDelete);
                      }}
                    >
                      Elimina manichino
                    </Button>
                  </Group>
                ) : null}
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        );
      })}
    </Accordion>
  );
}
